'use client';

import React from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { containerStagger, fadeUp } from '@/lib/animations';

/**
 * StorySection component
 *
 * A responsive section displaying Andoria company story with an image and descriptive text.
 *
 *
 * Features:
 * - Responsive layout with column stacking on smaller screens and row layout on larger screens.
 * - Background image with absolute positioning to ensure visual consistency.
 * - Styled heading with gradient text.
 * - Paragraph content summarizing company capabilities.
 *
 * Tailwind CSS utility classes are used for styling and responsive behavior.
 * The section is optimized for both mobile and desktop experiences.
 *
 * @returns {JSX.Element} A React component that renders the company story section.
 */
const StorySection: React.FC = () => {
  const reduce = useReducedMotion();

  return (
    <section className="py-18 px-6 mx-auto">
      <motion.div
        className="flex flex-col-reverse lg:flex-row gap-12 lg:gap-[142px] items-center mt-6 sm:mt-12 lg:mt-20"
        initial={reduce ? undefined : 'hidden'}
        whileInView={reduce ? undefined : 'visible'}
        viewport={{ once: true, amount: 0.18 }}
        variants={containerStagger}
      >
        {/* Image */}
        <motion.div
          className="relative flex justify-end items-center flex-shrink-0 w-full max-w-[566px] h-[200px] sm:h-[248px] rounded-[16px] bg-gray-400 bg-cover bg-no-repeat bg-center"
          variants={fadeUp}
        >
          <div className="absolute inset-0 rounded-[16px] overflow-hidden">
            <Image
              src="/images/about/andoria-ai-about-story.jpg"
              alt="Team collaboration"
              fill
              sizes="(max-width: 640px) 100vw, 566px"
              className="object-cover"
            />
          </div>
        </motion.div>

        {/* Text */}
        <motion.div className="w-full lg:w-1/2 space-y-6" variants={fadeUp}>
          <h2 className="text-[28px] sm:text-[36px] md:text-[42px] lg:text-[48px] font-semibold font-manrope tracking-tight leading-[1.2] sm:leading-[1.3] gradient-text bg-clip-text text-transparent">
            Here's A Story
          </h2>

          {/* Descriptive paragraph about the company mission and expertise */}
          <p className="text-base sm:text-lg font-medium text-text-primary leading-6 sm:leading-7 tracking-tight font-manrope text-justify">
            At Andoria AI, we help businesses solve complex marketplace and data challenges with
            smart, scalable solutions.
            <br /> <br />
            From streamlining payment systems and optimizing supply chains to unlocking real-time
            insights, we combine AI, cloud technology, and human expertise to solve region-specific
            challenges—helping businesses thrive in their unique markets. Faster decisions, sharper
            strategies, and a competitive edge – all powered by Advanced Intelligence and the right
            blend of expertise, execution, and measurable impact.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default StorySection;
